import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { CompanyService } from '../../services/company.service';

/**
 * "Plugin-Vorschau": a button that slides in a sheet with the real plugin as an
 * inline card. Members don't sell, so they get nothing.
 *
 * The plugin runs live, exactly as on the customer's website: it loads the saved
 * settings and products (with images) and checkout works. A warning says so, since
 * a purchase here is a real one. The sheet content is created on every open, so
 * the plugin always shows the last saved state.
 */
@Component({
  selector: 'app-plugin-preview-sheet',
  imports: [NgIcon, HlmAlertImports, HlmButtonImports, HlmSheetImports],
  providers: [provideIcons({ lucideEye, lucideTriangleAlert })],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (storeId(); as storeId) {
      <hlm-sheet
        side="right"
        [state]="open() ? 'open' : 'closed'"
        (stateChanged)="open.set($event === 'open')">
        <button hlmSheetTrigger hlmBtn variant="outline">
          <ng-icon name="lucideEye" />
          Plugin-Vorschau
        </button>

        <hlm-sheet-content
          *hlmSheetPortal
          class="gap-0 overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[32rem]">
          <div hlmSheetHeader class="border-b pe-12">
            <h2 hlmSheetTitle>Plugin-Vorschau</h2>
            <p hlmSheetDescription>
              So sehen Kunden deine Gutschein-App auf deiner Shopseite und deiner Website.
              Änderungen erscheinen erst nach dem Speichern.
            </p>
            <div hlmAlert variant="warning" class="mt-2">
              <ng-icon name="lucideTriangleAlert" />
              <p hlmAlertTitle>Käufe sind hier echt</p>
              <p hlmAlertDescription>
                Die Gutschein-App ist scharfgeschaltet. Schließt du hier einen Kauf ab, wird ein
                echter Gutschein verkauft und bezahlt.
              </p>
            </div>
          </div>
          <div class="p-4">
            <schenkwerk-voucher [storeId]="storeId"></schenkwerk-voucher>
          </div>
        </hlm-sheet-content>
      </hlm-sheet>
    }
  `,
})
export class PluginPreviewSheetComponent {
  private readonly company = inject(CompanyService).company;

  protected readonly open = signal(false);

  /** The store the plugin loads; null for members, who get no button at all. */
  protected readonly storeId = computed(() => {
    const company = this.company();
    return company && !company.is_member ? company.public_id : null;
  });
}
